/**
 * Rótulos + helpers de apresentação do explorador de auditoria (A5).
 * Sem dependência de React — testável isoladamente.
 */

import type { AuditEventWire, AuditSeverity } from './types';
import type { AuditTreeNode } from '@lume/ui-components';

/** Estilo Tailwind por severidade (badge). */
export const SEVERITY_BADGE: Record<AuditSeverity, string> = {
  debug: 'bg-gray-100 text-gray-600',
  info: 'bg-blue-100 text-blue-700',
  warning: 'bg-amber-100 text-amber-700',
  high: 'bg-orange-100 text-orange-700',
  forensic: 'bg-red-100 text-red-700',
};

export const SEVERITY_LABEL: Record<AuditSeverity, string> = {
  debug: 'depuração',
  info: 'informativo',
  warning: 'aviso',
  high: 'alto',
  forensic: 'forense',
};

/** Presets de período → quantos dias atrás (null = sem filtro). */
export interface PeriodPreset {
  id: string;
  label: string;
  days: number | null;
}

export const PERIOD_PRESETS: readonly PeriodPreset[] = [
  { id: 'all', label: 'Todo período', days: null },
  { id: '24h', label: 'Últimas 24h', days: 1 },
  { id: '7d', label: 'Últimos 7 dias', days: 7 },
  { id: '30d', label: 'Últimos 30 dias', days: 30 },
  { id: '90d', label: 'Últimos 90 dias', days: 90 },
];

/** Converte um preset de período em `since` ISO (ou undefined p/ "todo período"). */
export function periodToSince(presetId: string, now: Date = new Date()): string | undefined {
  const days = PERIOD_PRESETS.find((p) => p.id === presetId)?.days;
  if (days === null || days === undefined) return undefined;
  return new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString();
}

/** `true` quando o payload veio mascarado pelo serializer. */
export function isPayloadMasked(event: Pick<AuditEventWire, 'payload' | 'hasSensitive'>): boolean {
  return event.hasSensitive === true || event.payload?._masked === true;
}

/** Formata ISO → pt-BR curto (data + hora). */
export function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Formata ISO → só data (rótulo de bucket timeline). */
export function formatBucketLabel(iso: string, bucket: 'day' | 'hour'): string {
  const d = new Date(iso);
  return bucket === 'hour'
    ? d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Deriva o `operationType` do AuditTreeView a partir do eventType.
 * Mesma heurística canônica do `LgpdCascadePreview` (ADR-107).
 */
export function resolveOperationType(
  eventType: string,
): NonNullable<AuditTreeNode['operationType']> {
  if (eventType.includes('anon')) return 'anonymization';
  if (eventType.includes('delet') || eventType.includes('hard-delete')) return 'deletion';
  if (eventType.includes('redact')) return 'redaction';
  if (eventType.includes('resolv') || eventType.includes('conflict')) return 'resolution';
  return 'generic';
}

/**
 * Constrói os nós do AuditTreeView a partir da cascade real (`{ root, children }`).
 * Cascade backend agrupa por entityId → raiz única com os filhos aninhados.
 */
export function buildCascadeNodes(
  root: AuditEventWire,
  children: AuditEventWire[],
): AuditTreeNode[] {
  const toNode = (e: AuditEventWire): AuditTreeNode => ({
    id: e.id,
    label: e.eventType,
    timestamp: e.occurredAt,
    ...(e.actorUserId !== null ? { actor: e.actorUserId } : {}),
    sensitive: e.severity === 'high' || e.severity === 'forensic',
    operationType: resolveOperationType(e.eventType),
  });

  return [
    {
      ...toNode(root),
      children: children.map(toNode),
    },
  ];
}
